import React, { useState } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { useDownloadClientDocumentMutation } from 'services/api/clients.api';
import { Document } from 'services/types/document';
import { extractFilenameFromUrl, getFileExtensionFromUrl } from 'utils/fileUtils';

interface DocumentViewerProps {
  documents: Document[];
  clientId: number;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ documents, clientId }) => {
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [downloadDocument] = useDownloadClientDocumentMutation();

  const handleDownloadDocument = async (doc: Document): Promise<void> => {
    try {
      // Use the backend proxy endpoint for downloading
      const result = (await downloadDocument({
        clientId,
        documentId: doc.id,
      }).unwrap()) as Blob;

      // Determine filename
      let filename = doc.aiName;
      if (!filename) {
        const url = doc.url || doc.externalUrl;
        if (url) {
          filename = extractFilenameFromUrl(url);
        } else {
          filename = `document_${doc.id}`;
        }
      }
      const extension = getFileExtensionFromUrl(doc.url || doc.externalUrl || '') || 'pdf';
      const filenameWithExtension = `${filename}.${extension}`;

      // Create blob URL and download
      const blobUrl = window.URL.createObjectURL(result);
      const link = window.document.createElement('a');
      link.href = blobUrl;
      link.download = filenameWithExtension;
      window.document.body.appendChild(link);
      link.click();

      // Clean up
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading document:', error);

      // Fallback to direct URL if available
      const url = doc.url || doc.externalUrl;
      if (url) {
        window.open(url, '_blank');
      } else {
        // eslint-disable-next-line no-alert
        alert('Error al descargar el documento. Por favor, intenta de nuevo.');
      }
    }
  };

  const handleShowJson = (document: Document) => {
    setSelectedDocument(document);
    setJsonModalOpen(true);
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleString('es-CL');
  };

  const getDocumentIcon = (type: string | null) => {
    if (!type) return '📄';
    switch (type.toLowerCase()) {
      case 'pdf':
        return '📄';
      case 'image':
      case 'jpg':
      case 'jpeg':
      case 'png':
        return '🖼️';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      default:
        return '📄';
    }
  };

  if (documents.length === 0) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No hay documentos disponibles
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <List>
        {documents.map((document) => (
          <ListItem
            key={document.id}
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              mb: 1,
              flexDirection: 'column',
              alignItems: 'stretch',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center" flex={1}>
                <Typography variant="h6" sx={{ mr: 1 }}>
                  {getDocumentIcon(document.type)}
                </Typography>
                <ListItemText
                  primary={<Typography variant="subtitle2">{document.aiName || `Documento ${document.id}`}</Typography>}
                  secondary={`Creado: ${formatDate(document.createdAt)}`}
                />
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                {document.type && <Chip label={document.type.toUpperCase()} size="small" variant="outlined" />}
                <IconButton size="small" onClick={() => handleShowJson(document)} title="Ver datos en JSON">
                  <InfoIcon />
                </IconButton>
                {(document.url || document.externalUrl) && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      handleDownloadDocument(document).catch((err) => {
                        // eslint-disable-next-line no-console
                        console.error(err);
                      });
                    }}
                    title="Descargar documento"
                  >
                    <DownloadIcon />
                  </IconButton>
                )}
              </Box>
            </Box>
          </ListItem>
        ))}
      </List>

      {/* JSON Modal */}
      <Dialog open={jsonModalOpen} onClose={() => setJsonModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Datos del Documento: {selectedDocument?.aiName || `Documento ${selectedDocument?.id}`}
        </DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Información básica:
              </Typography>
              <Typography variant="body2">
                <strong>ID:</strong> {selectedDocument.id}
              </Typography>
              <Typography variant="body2">
                <strong>Tipo:</strong> {selectedDocument.type || 'No especificado'}
              </Typography>
              <Typography variant="body2">
                <strong>Nombre IA:</strong> {selectedDocument.aiName || 'No especificado'}
              </Typography>
              <Typography variant="body2">
                <strong>URL:</strong> {selectedDocument.url || 'No disponible'}
              </Typography>
              <Typography variant="body2">
                <strong>URL Externa:</strong> {selectedDocument.externalUrl || 'No disponible'}
              </Typography>
              <Typography variant="body2">
                <strong>Creado:</strong> {formatDate(selectedDocument.createdAt)}
              </Typography>
              <Typography variant="body2">
                <strong>Actualizado:</strong> {formatDate(selectedDocument.updatedAt)}
              </Typography>

              {selectedDocument.data && (
                <>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    Datos (JSON):
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', maxHeight: 400, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedDocument.data, null, 2)}
                    </pre>
                  </Paper>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedDocument?.data && (
            <Button
              onClick={() => {
                const dataStr = JSON.stringify(selectedDocument.data, null, 2);
                const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
                const downloadAnchorNode = window.document.createElement('a');
                downloadAnchorNode.setAttribute('href', dataUri);
                downloadAnchorNode.setAttribute('download', `document_${selectedDocument.id}_data.json`);
                window.document.body.appendChild(downloadAnchorNode);
                downloadAnchorNode.click();
                window.document.body.removeChild(downloadAnchorNode);
              }}
              startIcon={<DownloadIcon />}
            >
              Descargar JSON
            </Button>
          )}
          <Button onClick={() => setJsonModalOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentViewer;
